/**
=========================================================
* Material Dashboard 3 PRO React - v2.4.0
=========================================================

* Product Page: https://www.creative-tim.com/product/material-dashboard-pro-react
* Copyright 2024 Creative Tim (https://www.creative-tim.com)

Coded by www.creative-tim.com

 =========================================================

* The above copyright notice and this permission notice shall be included in all copies or substantial portions of the Software.
*/

// @mui material components
import Grid from "@mui/material/Grid";
import Divider from "@mui/material/Divider";
import Dialog from "@mui/material/Dialog";
import DialogTitle from "@mui/material/DialogTitle";
import DialogContent from "@mui/material/DialogContent";
import DialogActions from "@mui/material/DialogActions";
import Button from "@mui/material/Button";
import IconButton from "@mui/material/IconButton";
import CloseIcon from "@mui/icons-material/Close";
import CircularProgress from "@mui/material/CircularProgress";

// @mui icons
import FacebookIcon from "@mui/icons-material/Facebook";
import TwitterIcon from "@mui/icons-material/Twitter";
import InstagramIcon from "@mui/icons-material/Instagram";
import EditIcon from "@mui/icons-material/Edit";

// Material Dashboard 3 PRO React components
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import MDAvatar from "components/MDAvatar";
import { useState, useEffect, useRef } from "react";

// Material Dashboard 3 PRO React examples
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";
import Footer from "examples/Footer";
import ProfileInfoCard from "examples/Cards/InfoCards/ProfileInfoCard";

import ProfilesList from "layouts/pages/profile/profile-overview/components/ProfilesList";
import DefaultProjectCard from "examples/Cards/ProjectCards/DefaultProjectCard";

// Overview page components
import Header from "layouts/pages/profile/components/Header";
import PlatformSettings from "layouts/pages/profile/profile-overview/components/PlatformSettings";

// Images
import homeDecor1 from "assets/images/bg-player.jpeg";

import axios from "axios";
import { API_ESA_OFFICE, API_ESA_OFFICE_UPLOAD } from "utils";
import { logoutUser } from "actions/AuthAction";
import { useNavigate } from "react-router-dom";
import swal from "sweetalert";

function Overview() {
  const navigate = useNavigate();
  const [userId, setUserId] = useState(null);
  const [dinasId, setDinasId] = useState(null);
  const [uid_RSS, setUid_RSS] = useState(null);
  const [userDetail, setUserDetail] = useState(null);
  const [tugas, setTugas] = useState([]);
  const [openEditAvatarDialog, setOpenEditAvatarDialog] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [selectedFile, setSelectedFile] = useState(null);
  const [previewUrl, setPreviewUrl] = useState(null);
  const [isValidFile, setIsValidFile] = useState(false);
  const fileInputRef = useRef(null);
  useEffect(() => {
    const user = JSON.parse(localStorage.getItem("user"));
    if (user) {
      setUserId(user.uid);
      setDinasId(user.dinas_id);
      setUid_RSS(user.uid_RSS);
    } else {
      logoutUser(navigate);
    }
  }, []);

  // Fungsi untuk mengubah teks menjadi proper case
  const toProperCase = (text) => {
    if (!text) return "";
    return text
      .toLowerCase()
      .split(" ")
      .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
      .join(" ");
  };

  useEffect(() => {
    if (userId) {
      fetchUserDetail();
      fetchTugas();
    }
  }, [userId]);

  const fetchTugas = async () => {
    try {
      const response = await axios.get(
        `${API_ESA_OFFICE}users/tugas/${userId}`
      );
      setTugas(response.data);
    } catch (error) {
      console.error("Error fetching tugas:", error);
    }
  };

  const fetchUserDetail = async () => {
    try {
      const response = await axios.get(
        `${API_ESA_OFFICE}users/detail/${userId}`
      );
      setUserDetail(response.data);
      console.log("profil list ", response.data);
    } catch (error) {
      console.error("Error fetching user detail:", error);
    }
  };

  const handleOpenEditAvatarDialog = () => {
    setOpenEditAvatarDialog(true);
    setSelectedFile(null);
    setPreviewUrl(null);
    setIsValidFile(false);
  };

  const handleCloseEditAvatarDialog = () => {
    setOpenEditAvatarDialog(false);
    setSelectedFile(null);
    // Cleanup preview URL untuk menghindari memory leak
    if (previewUrl) {
      URL.revokeObjectURL(previewUrl);
    }
    setPreviewUrl(null);
    setIsValidFile(false);
    setUploading(false);
    // Reset file input
    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }
  };

  const handleFileSelect = (event) => {
    const file = event.target.files?.[0];

    // Cleanup preview URL sebelumnya jika ada
    if (previewUrl) {
      URL.revokeObjectURL(previewUrl);
    }

    if (!file) {
      setSelectedFile(null);
      setPreviewUrl(null);
      setIsValidFile(false);
      return;
    }

    // Validasi ukuran file (maksimal 1MB)
    if (file.size > 1 * 1024 * 1024) {
      swal({
        title: "Error",
        text: "Ukuran file melebihi 1 MB. Silakan pilih file yang lebih kecil",
        icon: "error",
      });
      setSelectedFile(null);
      setPreviewUrl(null);
      setIsValidFile(false);
      if (fileInputRef.current) {
        fileInputRef.current.value = "";
      }
      return;
    }

    // Validasi tipe file
    const validTypes = ["image/jpeg", "image/jpg", "image/png"];
    if (!validTypes.includes(file.type)) {
      swal({
        title: "Error",
        text: "Format file tidak didukung. Hanya JPG dan PNG yang diperbolehkan",
        icon: "error",
      });
      setSelectedFile(null);
      setPreviewUrl(null);
      setIsValidFile(false);
      if (fileInputRef.current) {
        fileInputRef.current.value = "";
      }
      return;
    }

    // File valid
    setSelectedFile(file);
    setIsValidFile(true);

    // Buat preview URL menggunakan createObjectURL untuk efisiensi
    const objectUrl = URL.createObjectURL(file);
    setPreviewUrl(objectUrl);
  };

  const handleEditIconClick = () => {
    fileInputRef.current?.click();
  };

  const handleUploadAvatar = async () => {
    if (!selectedFile || !uid_RSS) {
      swal({
        title: "Error",
        text: "Silakan pilih gambar terlebih dahulu",
        icon: "error",
      });
      return;
    }

    setUploading(true);
    try {
      // Buat File object baru dengan nama yang diubah menjadi uid_RSS
      // Backend menggunakan path.parse(file.originalname).name, jadi hanya nama tanpa extension
      // Tapi kita tetap perlu extension untuk type file, jadi kita gunakan extension asli
      const fileExtension = selectedFile.name.split(".").pop().toLowerCase();
      const validExtension = ["jpg", "jpeg", "png"].includes(fileExtension)
        ? fileExtension === "jpeg"
          ? "jpg"
          : fileExtension
        : "jpg"; // Default ke jpg jika extension tidak valid

      // Buat File baru dengan nama uid_RSS (tanpa extension di nama, tapi tetap ada extension untuk type)
      // Backend akan mengambil path.parse(file.originalname).name yang akan menghasilkan "uid_RSS"
      const renamedFile = new File(
        [selectedFile],
        `${uid_RSS}.${validExtension}`,
        {
          type: selectedFile.type,
          lastModified: selectedFile.lastModified,
        }
      );

      const formData = new FormData();
      // Backend menggunakan .array('photo', 1), jadi kita append sebagai array dengan 1 file
      formData.append("photo", renamedFile);

      const uploadResponse = await axios.post(
        `https://smartdh.rotendaokab.go.id/api/users/upload-avatar`,
        formData,
        {
          headers: { "Content-Type": "multipart/form-data" },
        }
      );

      if (uploadResponse.data && uploadResponse.data.success === 1) {
        // Cleanup preview URL sebelum close
        if (previewUrl) {
          URL.revokeObjectURL(previewUrl);
        }

        swal({
          title: "Berhasil!",
          text:
            uploadResponse.data.message || "Gambar profil berhasil diupdate",
          icon: "success",
          button: "OK",
        });

        // Refresh user detail untuk mendapatkan gambar baru
        await fetchUserDetail();
        handleCloseEditAvatarDialog();
      } else {
        throw new Error(
          uploadResponse.data?.message || "Gagal mengupload gambar"
        );
      }
    } catch (error) {
      console.error("Error uploading avatar:", error);
      swal({
        title: "Error!",
        text: error.response?.data?.message || "Gagal mengupload gambar profil",
        icon: "error",
        button: "OK",
      });
    } finally {
      setUploading(false);
    }
  };

  return (
    <DashboardLayout>
      <DashboardNavbar />
      <MDBox mb={2} />
      <Header
        userNama={userDetail && userDetail.nama}
        userJabatan={userDetail && userDetail.nama_jabatan}
        profile={
          userDetail &&
          `https://smartdh.rotendaokab.go.id/uploads/${userDetail.uid_RSS}.jpg`
        }
        onAvatarClick={handleOpenEditAvatarDialog}
      >
        <MDBox mt={5} mb={3}>
          <Grid container spacing={1}>
            <Grid item xs={12} md={6} xl={4}>
              <PlatformSettings />
            </Grid>
            <Grid item xs={12} md={6} xl={4} sx={{ display: "flex" }}>
              <Divider orientation="vertical" sx={{ ml: -2, mr: 1 }} />
              <ProfileInfoCard
                title="profile information"
                description="Seorang Aparatur Sipil Negara yang berdedikasi tinggi, selalu berkomitmen untuk memberikan pelayanan terbaik kepada masyarakat dan berkontribusi positif dalam pembangunan bangsa."
                info={{
                  fullName: userDetail && toProperCase(userDetail.nama),
                  mobile: userDetail && userDetail.no_hp,
                  email: userDetail && userDetail.email,
                  Dinas: userDetail && toProperCase(userDetail.nama_dinas),
                }}
                social={[
                  {
                    link: `https://www.facebook.com/${
                      userDetail && userDetail.facebook
                    }`,
                    icon: <FacebookIcon />,
                    color: "facebook",
                  },
                  {
                    link: `https://twitter.com/${
                      userDetail && userDetail.instagram
                    }`,
                    icon: <InstagramIcon />,
                    color: "instagram",
                  },
                ]}
                action={{ route: "", tooltip: "Edit Profile" }}
                shadow={false}
              />
              <Divider orientation="vertical" sx={{ mx: 0 }} />
            </Grid>
            <Grid item xs={12} xl={4}>
              <ProfilesList
                title={userDetail && userDetail.nama_bidang}
                profiles={userDetail ? userDetail.teman_bidang : []}
                shadow={false}
              />
            </Grid>
          </Grid>
        </MDBox>
        <MDBox pt={2} px={2} lineHeight={1.25}>
          <MDTypography variant="h6" fontWeight="medium">
            Tugas
          </MDTypography>
          <MDBox mb={1}>
            <MDTypography variant="button" color="text">
              Pelaksanaan Instruksi Pimpinan
            </MDTypography>
          </MDBox>
        </MDBox>
        <MDBox p={2}>
          <Grid container spacing={6}>
            {tugas &&
              tugas.map((tugas) => (
                <Grid item xs={12} md={6} xl={3} key={tugas.id}>
                  <DefaultProjectCard
                    image={homeDecor1}
                    label={`Prioritas: ${tugas.tugas.prioritas}`}
                    title={tugas.tugas.judul}
                    description={
                      <MDTypography
                        variant="body2"
                        color="text"
                        sx={{
                          whiteSpace: "pre-wrap",
                          wordBreak: "break-word",
                          maxHeight: "100px",
                          overflow: "auto",
                          "& p": {
                            margin: 0,
                            padding: 0,
                          },
                        }}
                        dangerouslySetInnerHTML={{
                          __html: tugas.tugas.deskripsi,
                        }}
                      />
                    }
                    action={{
                      type: "internal",
                      route: "/pages/profile/profile-overview",
                      color: "info",
                      label: "lihat tugas",
                    }}
                    authors={tugas.penerima_instruksi_yang_sama.map(
                      (penerima) => ({
                        image: `https://smartdh.rotendaokab.go.id/uploads/${penerima.uid_RSS}.jpg`,
                        name: penerima.nama,
                      })
                    )}
                  />
                </Grid>
              ))}
          </Grid>
        </MDBox>
      </Header>

      {/* Dialog Edit Avatar */}
      <Dialog
        open={openEditAvatarDialog}
        onClose={handleCloseEditAvatarDialog}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          <MDBox
            display="flex"
            justifyContent="space-between"
            alignItems="center"
          >
            <MDTypography variant="h6">Edit Gambar Profil</MDTypography>
            <IconButton onClick={handleCloseEditAvatarDialog}>
              <CloseIcon />
            </IconButton>
          </MDBox>
        </DialogTitle>
        <DialogContent>
          <MDBox pt={2} pb={3} px={3}>
            <MDBox
              display="flex"
              flexDirection="column"
              alignItems="center"
              justifyContent="center"
              mb={3}
            >
              <MDBox position="relative" display="inline-block">
                <MDAvatar
                  src={
                    previewUrl ||
                    (userDetail &&
                      `https://smartdh.rotendaokab.go.id/uploads/${userDetail.uid_RSS}.jpg`)
                  }
                  alt="profile-preview"
                  size="xxl"
                  shadow="md"
                  sx={{
                    width: 150,
                    height: 150,
                  }}
                />
                <IconButton
                  onClick={handleEditIconClick}
                  sx={{
                    position: "absolute",
                    bottom: 0,
                    right: 0,
                    backgroundColor: "info.main",
                    color: "white",
                    "&:hover": {
                      backgroundColor: "info.dark",
                    },
                    width: 40,
                    height: 40,
                  }}
                >
                  <EditIcon />
                </IconButton>
                <input
                  ref={fileInputRef}
                  type="file"
                  accept="image/jpeg,image/jpg,image/png"
                  onChange={handleFileSelect}
                  style={{ display: "none" }}
                />
              </MDBox>
              <MDBox mt={2} textAlign="center">
                <MDTypography
                  variant="button"
                  fontWeight="regular"
                  color="text"
                  mb={1}
                >
                  Pilih Gambar Profil
                </MDTypography>
                <MDTypography
                  variant="caption"
                  color="text.secondary"
                  display="block"
                >
                  Format: JPG, PNG | Maksimal: 1 MB
                </MDTypography>
              </MDBox>
              {selectedFile && (
                <MDBox mt={2} textAlign="center">
                  <MDTypography variant="caption" color="text.secondary">
                    File: {selectedFile.name} (
                    {(selectedFile.size / 1024).toFixed(2)} KB)
                  </MDTypography>
                </MDBox>
              )}
            </MDBox>
          </MDBox>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseEditAvatarDialog} disabled={uploading}>
            Batal
          </Button>
          <Button
            onClick={handleUploadAvatar}
            variant="contained"
            color="info"
            disabled={uploading || !isValidFile}
            startIcon={
              uploading ? <CircularProgress size={16} color="inherit" /> : null
            }
          >
            {uploading ? "Mengupload..." : "Simpan"}
          </Button>
        </DialogActions>
      </Dialog>

      <Footer />
    </DashboardLayout>
  );
}

export default Overview;
